import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { opportunityApi } from '../../services/api';
import { Opportunity } from '../../types';
import { 
  MagnifyingGlassIcon, 
  StarIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  FunnelIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';

const PROCUREMENT_TYPES = [
  { code: 'Solicitation', label: 'Solicitation' },
  { code: 'Presolicitation', label: 'Pre-solicitation' },
  { code: 'Combined Synopsis/Solicitation', label: 'Combined Synopsis/Solicitation' },
  { code: 'Award Notice', label: 'Award Notice' },
  { code: 'Sources Sought', label: 'Sources Sought' },
  { code: 'Special Notice', label: 'Special Notice' },
  { code: 'Justification', label: 'Justification (J&A)' },
  { code: 'Intent to Bundle', label: 'Intent to Bundle' },
  { code: 'Sale of Surplus Property', label: 'Sale of Surplus Property' },
];

const SET_ASIDE_TYPES = [
  { code: 'SBA', label: 'Total Small Business Set-Aside' },
  { code: 'SBP', label: 'Partial Small Business Set-Aside' },
  { code: '8A', label: '8(a) Set-Aside' },
  { code: '8AN', label: '8(a) Sole Source' },
  { code: 'HZC', label: 'HUBZone Set-Aside' },
  { code: 'HZS', label: 'HUBZone Sole Source' },
  { code: 'SDVOSBC', label: 'SDVOSB Set-Aside' },
  { code: 'SDVOSBS', label: 'SDVOSB Sole Source' },
  { code: 'WOSB', label: 'WOSB Set-Aside' },
  { code: 'WOSBSS', label: 'WOSB Sole Source' },
  { code: 'EDWOSB', label: 'EDWOSB Set-Aside' },
  { code: 'EDWOSBSS', label: 'EDWOSB Sole Source' },
  { code: 'LAS', label: 'Local Area Set-Aside' },
  { code: 'IEE', label: 'Indian Economic Enterprise' },
  { code: 'ISBEE', label: 'Indian Small Business Economic Enterprise' },
  { code: 'BICiv', label: 'Buy Indian Set-Aside' },
  { code: 'VSA', label: 'VA Veteran-Owned Set-Aside' },
  { code: 'VSS', label: 'VA Veteran-Owned Sole Source' },
];

interface Filters {
  ptype: string;
  setAside: string;
  agency: string;
  naicsCode: string;
}

const timeAgo = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const timeFromNow = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs < 0) return 'Expired';
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return '< 1d';
  if (diffDays === 1) return '1 day';
  if (diffDays < 7) return `${diffDays} days`;
  return `${Math.floor(diffDays / 7)}w left`;
};

const Opportunities: React.FC = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLimited, setIsLimited] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [summaries, setSummaries] = useState<Record<string, string>>({});
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  
  const [filters, setFilters] = useState<Filters>({
    ptype: 'Solicitation', setAside: '', agency: '', naicsCode: '',
  });
  
  const [pagination, setPagination] = useState({
    page: 1, limit: 25, total: 0, pages: 1,
  });

  const isFreePlan = user?.plan === 'FREE';

  const buildParams = useCallback((page: number, limit: number) => {
    const params: any = { page, limit };
    if (searchQuery) params.q = searchQuery;
    if (filters.agency) params.agency = filters.agency;
    if (filters.setAside) params.setAside = filters.setAside;
    if (filters.naicsCode) params.naicsCode = filters.naicsCode;
    // Always use the selected ptype (default is 'o' for Solicitation)
    params.ptype = filters.ptype;
    return params;
  }, [searchQuery, filters]);

  const fetchOpportunities = useCallback(async (page = pagination.page, limit = pagination.limit) => {
    try {
      setLoading(true);
      const params = buildParams(page, limit);
      const response = await opportunityApi.search(params);
      const opps = response.data.opportunities || [];
      setOpportunities(opps);
      setIsLimited(response.data.isLimited || false);
      setUpgradeMessage(response.data.upgradeMessage || '');
      
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages,
        });
      }

      if (opps.length > 0) generateNLPSummaries(opps);
    } catch (error: any) {
      console.error('Failed to fetch opportunities:', error);
      if (error.response?.status === 403) setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams, pagination.page, pagination.limit]);

  const generateNLPSummaries = async (opps: Opportunity[]) => {
    setLoadingSummaries(true);
    try {
      // Generate summaries for ALL visible opportunities in batches
      const batchSize = 5;
      for (let i = 0; i < opps.length; i += batchSize) {
        const batch = opps.slice(i, i + batchSize);
        
        await Promise.all(
          batch.map(async (opp) => {
            try {
              // Check if we already have this summary cached
              if (summaries[opp.id]) return;
              
              const response = await opportunityApi.getNLPSummary(opp.id);
              setSummaries(prev => ({ ...prev, [opp.id]: response.data.summary }));
            } catch {
              // Silently fail for individual summaries
            }
          })
        );
        
        // Small delay between batches
        if (i + batchSize < opps.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    } finally {
      setLoadingSummaries(false);
    }
  };

  useEffect(() => { fetchOpportunities(1, 25); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') fetchOpportunities(1, pagination.limit);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) fetchOpportunities(newPage, pagination.limit);
  };

  const handleLimitChange = (newLimit: number) => fetchOpportunities(1, newLimit);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOpportunities(1, pagination.limit);
  };

  const handleFilterApply = () => {
    setIsFilterModalOpen(false);
    fetchOpportunities(1, pagination.limit);
  };

  const handleFilterClear = () => {
    setFilters({ ptype: 'Solicitation', setAside: '', agency: '', naicsCode: '' });
    setSearchQuery('');
    fetchOpportunities(1, pagination.limit);
  };

  const activeFiltersCount = [
    filters.ptype && filters.ptype !== 'Solicitation' ? 1 : 0, filters.setAside ? 1 : 0,
    filters.agency ? 1 : 0, filters.naicsCode ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contract Opportunities</h1>
          <p className="text-sm text-gray-500">Search and track federal contract opportunities from SAM.gov</p>
        </div>
      </div>

      {isFreePlan && (
        <div className="bg-gradient-to-r from-brand-600 to-purple-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Free Plan - Limited Results</p>
              <p className="text-sm text-brand-100">Upgrade to Pro for unlimited searches and advanced filters</p>
            </div>
            <Link to="/pricing" className="bg-white text-brand-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-50">Upgrade</Link>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search by keyword, agency, NAICS code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setIsFilterModalOpen(true)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
              <AdjustmentsHorizontalIcon className="h-5 w-5" /> Filters
              {activeFiltersCount > 0 && <span className="bg-brand-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeFiltersCount}</span>}
            </button>
            <select value={pagination.limit} onChange={(e) => handleLimitChange(Number(e.target.value))} className="px-3 py-2 pr-8 border border-gray-300 rounded-lg appearance-none bg-white" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={250}>250 / page</option>
            </select>
            <button type="submit" className="btn-primary flex items-center gap-2"><MagnifyingGlassIcon className="h-5 w-5" /> Search</button>
          </div>
        </form>
        
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">Active filters:</span>
            {filters.ptype && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Type: {PROCUREMENT_TYPES.find(t => t.code === filters.ptype)?.label}<button onClick={() => setFilters(f => ({ ...f, ptype: '' }))}><XMarkIcon className="h-3 w-3" /></button></span>}
            {filters.setAside && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Set-Aside: {SET_ASIDE_TYPES.find(s => s.code === filters.setAside)?.label}<button onClick={() => setFilters(f => ({ ...f, setAside: '' }))}><XMarkIcon className="h-3 w-3" /></button></span>}
            {filters.naicsCode && <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">NAICS: {filters.naicsCode}<button onClick={() => setFilters(f => ({ ...f, naicsCode: '' }))}><XMarkIcon className="h-3 w-3" /></button></span>}
            <button onClick={handleFilterClear} className="text-xs text-gray-500 hover:text-gray-700 underline">Clear all</button>
          </div>
        )}
      </div>

      {upgradeMessage && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"><p className="text-sm text-yellow-800">{upgradeMessage}</p></div>}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> - <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div></div>
      ) : opportunities.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No opportunities found</h3>
          <p className="mt-1 text-gray-500">Try adjusting your search terms or filters</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-72">Opportunity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-44">Agency</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">NAICS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Posted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Due</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"><div className="flex items-center gap-1"><DocumentTextIcon className="h-4 w-4 text-gray-400" />Quick Summary</div></th>
                  <th className="relative px-4 py-3 w-16"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {opportunities.map((opp) => (
                  <tr key={opp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <Link to={`/opportunities/${opp.id}`} className="text-sm font-medium text-brand-600 hover:text-brand-900 line-clamp-2" title={opp.title}>{opp.title}</Link>
                      <p className="text-xs text-gray-500 mt-1">{opp.solicitationNumber && <span className="mr-2">{opp.solicitationNumber}</span>}{opp.setAside && <span className="text-purple-600">{opp.setAside}</span>}</p>
                    </td>
                    <td className="px-4 py-4"><div className="text-sm text-gray-900 truncate max-w-[160px]" title={opp.agency}>{opp.agency}</div>{opp.subAgency && <div className="text-xs text-gray-500 truncate max-w-[160px]" title={opp.subAgency}>{opp.subAgency}</div>}</td>
                    <td className="px-4 py-4"><span className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">{opp.naicsCode || 'N/A'}</span></td>
                    <td className="px-4 py-4"><span className="text-sm text-gray-900">{timeAgo(opp.postedDate)}</span></td>
                    <td className="px-4 py-4"><span className={`text-sm font-medium ${new Date(opp.responseDeadline) < new Date() ? 'text-red-600' : new Date(opp.responseDeadline).getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000 ? 'text-orange-600' : 'text-green-600'}`}>{timeFromNow(opp.responseDeadline)}</span></td>
                    <td className="px-4 py-4"><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{opp.type || 'Solicitation'}</span></td>
                    <td className="px-4 py-4">{summaries[opp.id] ? <p className="text-xs text-gray-600 line-clamp-3" title={summaries[opp.id]}>{summaries[opp.id]}</p> : loadingSummaries ? <div className="flex items-center gap-2 text-xs text-gray-400"><div className="animate-spin h-3 w-3 border border-gray-300 border-t-blue-500 rounded-full"></div>Analyzing...</div> : <span className="text-xs text-gray-400">-</span>}</td>
                    <td className="px-4 py-4 text-right"><button className="text-gray-400 hover:text-gray-500"><StarIcon className="h-5 w-5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {!isFreePlan && pagination.pages > 1 && (
            <div className="px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"><ChevronLeftIcon className="h-5 w-5" /></button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) pageNum = i + 1;
                      else if (pagination.page <= 3) pageNum = i + 1;
                      else if (pagination.page >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                      else pageNum = pagination.page - 2 + i;
                      return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-10 h-10 rounded-lg text-sm font-medium ${pagination.page === pageNum ? 'bg-brand-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{pageNum}</button>;
                    })}
                  </div>
                  <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.pages} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"><ChevronRightIcon className="h-5 w-5" /></button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b"><h2 className="text-lg font-semibold flex items-center gap-2"><FunnelIcon className="h-5 w-5" />Advanced Filters</h2><button onClick={() => setIsFilterModalOpen(false)}><XMarkIcon className="h-6 w-6 text-gray-400" /></button></div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Procurement Type</label><select value={filters.ptype} onChange={(e) => setFilters(f => ({ ...f, ptype: e.target.value || 'Solicitation' }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg appearance-none bg-white" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}><option value="Solicitation">Solicitation (Default)</option><option value="">All Types</option>{PROCUREMENT_TYPES.map((type) => <option key={type.code} value={type.code}>{type.label}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Set-Aside Type</label><select value={filters.setAside} onChange={(e) => setFilters(f => ({ ...f, setAside: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg appearance-none bg-white" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }} disabled={isFreePlan}><option value="">All Set-Asides</option>{SET_ASIDE_TYPES.map((sa) => <option key={sa.code} value={sa.code}>{sa.label}</option>)}</select>{isFreePlan && <p className="text-xs text-gray-500 mt-1">Set-aside filtering requires Pro plan</p>}</div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">NAICS Code</label><input type="text" placeholder="e.g., 541512" value={filters.naicsCode} onChange={(e) => setFilters(f => ({ ...f, naicsCode: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Agency/Organization</label><input type="text" placeholder="e.g., Department of Defense" value={filters.agency} onChange={(e) => setFilters(f => ({ ...f, agency: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 border-t bg-gray-50"><button onClick={handleFilterClear} className="text-gray-600 hover:text-gray-800 font-medium">Clear All</button><div className="flex gap-2"><button onClick={() => setIsFilterModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button><button onClick={handleFilterApply} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">Apply Filters</button></div></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Opportunities;
